import { moveItemInArray } from '@angular/cdk/drag-drop';
import { createReducer, on } from '@ngrx/store';
import { cloneDeep } from 'lodash';

import { Headline } from '@ninety/platform/models';

import { CascadingMessagesStateActions } from '../cascading-messages/cascading-messages-state.actions';

import { HeadlinesStateActions } from './headlines-state.actions';
import { INLINE_TEMP_HEADLINE_ID } from './headlines-state.model';
import {
  defaultPagination,
  defaultSort,
  headlinesInitialState,
  headlinesStateAdapter,
  HeadlinesStateModel,
} from './headlines-state.model';

export const HeadlinesStateReducer = createReducer(
  headlinesInitialState,
  on(
    HeadlinesStateActions.hydrate,
    (state, { pageSize, sort }): HeadlinesStateModel => ({
      ...state,
      pagination: {
        ...state.pagination,
        ...(pageSize && { size: pageSize }),
      },
      ...(sort && { sort }),
    })
  ),

  on(HeadlinesStateActions.get, (state): HeadlinesStateModel => ({ ...state, loading: true, error: false })),

  on(HeadlinesStateActions.getSuccess, (state, { headlines, meeting }) => {
    let headlinesToSet: Headline[];
    if (meeting && meeting?.doneHeadlines.length) {
      headlinesToSet = cloneDeep(headlines.data);
      headlinesToSet.forEach(headline => {
        if (meeting.doneHeadlines.includes(headline._id)) headline.isDone = true;
      });
    }
    headlinesToSet = headlinesToSet ?? headlines.data;

    return headlinesStateAdapter.setAll(headlinesToSet, {
      ...state,
      totalCount: headlines.totalCount,
      loading: false,
      error: false,
    });
  }),

  on(
    HeadlinesStateActions.getFailed,
    (state): HeadlinesStateModel => ({
      ...state,
      loading: false,
      error: true,
    })
  ),
  on(
    HeadlinesStateActions.filterByTeam,
    (state, { teamId }): HeadlinesStateModel => ({
      ...state,
      filters: { ...state.filters, teamId },
      pagination: {
        ...defaultPagination,
        size: state.pagination.size,
      },
      loading: true,
    })
  ),
  on(
    HeadlinesStateActions.showArchived,
    (state, { archived }): HeadlinesStateModel => ({
      ...state,
      selectedId: null,
      filters: { ...state.filters, archived },
      pagination: {
        ...defaultPagination,
        size: state.pagination.size,
      },
      loading: true,
    })
  ),
  on(
    HeadlinesStateActions.search,
    (state, { searchText }): HeadlinesStateModel => ({
      ...state,
      filters: { ...state.filters, searchText },
      pagination: {
        ...defaultPagination,
        size: state.pagination.size,
      },
      loading: true,
    })
  ),
  on(
    HeadlinesStateActions.sortChange,
    (state, { sort }): HeadlinesStateModel => ({
      ...state,
      sort,
    })
  ),
  on(
    HeadlinesStateActions.setInMeetingId,
    (state, { meetingId }): HeadlinesStateModel => ({
      ...state,
      filters: { ...state.filters, inMeetingId: meetingId, includeDiscussed: !meetingId },
    })
  ),

  on(
    HeadlinesStateActions.clearSort,
    (state): HeadlinesStateModel => ({
      ...state,
      sort: defaultSort,
    })
  ),
  on(HeadlinesStateActions.updateOrdinals, (state, { previousIndex, currentIndex }): HeadlinesStateModel => {
    const headlinesToBeOrdered = cloneDeep(headlinesStateAdapter.getSelectors().selectAll(state));
    moveItemInArray(headlinesToBeOrdered, previousIndex, currentIndex);
    headlinesToBeOrdered.map((headline, i) => (headline.ordinal = state.pagination.index * state.pagination.size + i));
    return headlinesStateAdapter.setAll(headlinesToBeOrdered, state);
  }),
  on(
    HeadlinesStateActions.openInDetailView,
    (state, { headlineId }): HeadlinesStateModel => ({
      ...state,
      selectedId: headlineId,
    })
  ),
  on(HeadlinesStateActions.setSelected, (state, { selectedId }): HeadlinesStateModel => ({ ...state, selectedId })),
  on(
    HeadlinesStateActions.clearSelected,
    CascadingMessagesStateActions.setSelected,
    (state): HeadlinesStateModel => ({ ...state, selectedId: null })
  ),
  on(
    HeadlinesStateActions.paginationChange,
    (state, { index, size }): HeadlinesStateModel => ({
      ...state,
      pagination: { index, size },
    })
  ),
  on(
    HeadlinesStateActions.update,
    HeadlinesStateActions.updateLocal,
    HeadlinesStateActions.setCompletedSuccess,
    HeadlinesStateActions.setUserSuccess,
    (state, { update }) => {
      return headlinesStateAdapter.updateOne(update, state);
    }
  ),

  on(HeadlinesStateActions.setArchivedSuccess, (state, { id }) =>
    headlinesStateAdapter.removeOne(id, { ...state, totalCount: state.totalCount - 1 })
  ),

  on(HeadlinesStateActions.delete, HeadlinesStateActions.setTeam, (state, { id }) =>
    headlinesStateAdapter.removeOne(id, { ...state, totalCount: state.totalCount - 1 })
  ),
  on(HeadlinesStateActions.deleteLocal, (state, { id }) =>
    headlinesStateAdapter.removeOne(id, { ...state, totalCount: state.totalCount - 1 })
  ),
  on(
    HeadlinesStateActions.resetStore,
    (state): HeadlinesStateModel => ({
      ...headlinesInitialState,
      pagination: { ...headlinesInitialState.pagination, size: state.pagination.size }, //reset to the page size saved in local storage
    })
  ),
  on(HeadlinesStateActions.add, HeadlinesStateActions.addLocal, (state, { headline }) =>
    headlinesStateAdapter.addOne(headline, { ...state, totalCount: state.totalCount + 1 })
  ),
  on(HeadlinesStateActions.addInline, (state, { headline }) =>
    headlinesStateAdapter.addOne(headline, {
      ...state,
      focusOnInlineAdd: false,
      listControlsDisabled: true,
    })
  ),
  on(HeadlinesStateActions.cancelAddInline, state =>
    headlinesStateAdapter.removeOne(INLINE_TEMP_HEADLINE_ID, {
      ...state,
      listControlsDisabled: false,
    })
  ),

  on(HeadlinesStateActions.saveInlineSuccess, (state, { headline }) =>
    headlinesStateAdapter.setOne(headline, { ...state, totalCount: state.totalCount + 1, focusOnInlineAdd: true })
  ),

  on(HeadlinesStateActions.addAttachment, (state, { id, attachment }) => {
    const headline = state.entities[id];
    if (headline) {
      const updatedAttachments = [...headline.attachments, attachment];
      return headlinesStateAdapter.updateOne({ id, changes: { attachments: updatedAttachments } }, state);
    }
    return state;
  }),

  on(HeadlinesStateActions.removeAttachment, (state, { id, attachmentId }) => {
    const headline = state.entities[id];
    if (headline?.attachments?.length) {
      const updatedAttachments = headline.attachments.filter(attachment => attachment._id !== attachmentId);
      return headlinesStateAdapter.updateOne({ id, changes: { attachments: updatedAttachments } }, state);
    }
    return state;
  }),

  /** Attachments */
  on(HeadlinesStateActions.attachmentUploaded, (state, { event }) => {
    const headline = state.entities[event.attachment.parentId];
    if (headline) {
      const attachments = [...headline.attachments, event.attachment];
      return headlinesStateAdapter.updateOne({ id: event.attachment.parentId, changes: { attachments } }, state);
    }
    return state;
  }),
  on(HeadlinesStateActions.attachmentRemoved, (state, { event }) => {
    const headline = state.entities[event.attachment.parentId];
    if (headline) {
      const attachments = headline.attachments?.filter(a => a._id !== event.attachment._id) ?? [];
      return headlinesStateAdapter.updateOne({ id: event.attachment.parentId, changes: { attachments } }, state);
    }
    return state;
  }),

  on(
    HeadlinesStateActions.setShowTrash,
    (state, { show }): HeadlinesStateModel => ({
      ...state,
      showTrash: show,
      ...(!show ? { deletedHeadlinesLoadStarted: false } : null),
    })
  ),

  on(
    HeadlinesStateActions.getDeletedHeadlines,
    (state): HeadlinesStateModel => ({
      ...state,
      deletedHeadlinesLoadStarted: true,
      deletedHeadlinesLoading: true,
      error: false,
    })
  ),
  on(
    HeadlinesStateActions.getDeletedHeadlinesSuccess,
    (state, { headlines, totalCount }): HeadlinesStateModel => ({
      ...headlinesStateAdapter.setAll(headlines, state),
      totalCount,
      deletedHeadlinesLoading: false,
      error: false,
      selectedId: null,
    })
  ),
  on(
    HeadlinesStateActions.getDeletedHeadlinesFailed,
    (state): HeadlinesStateModel => ({ ...state, deletedHeadlinesLoading: false, error: true })
  ),

  on(
    HeadlinesStateActions.restoreHeadline,
    HeadlinesStateActions.permanentDeleteHeadline,
    (state): HeadlinesStateModel => ({ ...state, deletedHeadlinesLoading: true, error: false })
  ),

  on(
    HeadlinesStateActions.restoreHeadlineSuccess,
    HeadlinesStateActions.permanentDeleteHeadlineSuccess,
    (state, { headlineId }): HeadlinesStateModel => ({
      ...headlinesStateAdapter.removeOne(headlineId, state),
      totalCount: Math.max(0, state.totalCount - 1),
      deletedHeadlinesLoading: false,
      error: false,
    })
  ),

  on(
    HeadlinesStateActions.restoreHeadlineFailed,
    HeadlinesStateActions.permanentDeleteHeadlineFailed,
    (state): HeadlinesStateModel => ({ ...state, deletedHeadlinesLoading: false, error: true })
  )
);
